import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Scanner } from '@yudiel/react-qr-scanner';
import '../styles/MobileQRScanner.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MobileQRScanner = ({ onScan, onError, isActive = true }) => {
  const [error, setError] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [lastScan, setLastScan] = useState('');
  const [hasPermission, setHasPermission] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const scanTimeoutRef = useRef();

  // Check for secure context and camera permissions
  useEffect(() => {
    const checkPermissions = async () => {
      setIsLoading(true);
      
      // Check for secure context
      if (window.location.protocol !== 'https:' && 
          !['localhost', '127.0.0.1'].includes(window.location.hostname)) {
        setError('Camera access requires HTTPS or localhost');
        setIsLoading(false);
        return;
      }

      // Check for camera availability
      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error('Camera API not supported');
        }

        // Request camera permission
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        stream.getTracks().forEach(track => track.stop());
        
        setHasPermission(true);
        setError('');
      } catch (err) {
        let errorMessage = 'Camera access denied or not available';
        
        if (err.name === 'NotAllowedError') {
          errorMessage = 'Camera permission denied. Please allow camera access and refresh.';
        } else if (err.name === 'NotFoundError') {
          errorMessage = 'No camera found on this device.';
        } else if (err.name === 'NotSupportedError') {
          errorMessage = 'Camera not supported on this device.';
        }
        
        setError(errorMessage);
        onError?.(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    checkPermissions();
  }, [onError]);

  const handleScan = useCallback((result) => {
    if (!result || !result[0]?.rawValue || !isActive || isScanning) return;
    
    const scannedData = result[0].rawValue;
    
    // Prevent duplicate scans
    if (scannedData === lastScan) return;
    
    setLastScan(scannedData);
    setIsScanning(true);
    
    // Clear any existing timeout
    if (scanTimeoutRef.current) {
      clearTimeout(scanTimeoutRef.current);
    }
    
    // Provide haptic feedback on mobile
    if ('vibrate' in navigator) {
      navigator.vibrate(100);
    }
    
    onScan(scannedData);
    
    // Reset scanning state after delay
    scanTimeoutRef.current = setTimeout(() => {
      setIsScanning(false);
      setLastScan('');
    }, 2000);
  }, [isActive, isScanning, lastScan, onScan]);

  const handleError = useCallback((error) => {
    console.error('Scanner error:', error);
    const errorMessage = 'Error accessing camera or scanning QR code';
    setError(errorMessage);
    onError?.(errorMessage);
  }, [onError]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
      }
    };
  }, []);

  if (isLoading) {
    return (
      <div className="mobile-scanner-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Initializing camera...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mobile-scanner-container">
        <div className="error-container">
          <div className="error-icon">📷</div>
          <h3>Camera Access Required</h3>
          <p className="error-message">{error}</p>
          {window.location.protocol !== 'https:' && (
            <div className="https-notice">
              <p>💡 <strong>Tip:</strong> Camera access requires HTTPS for security. Please access this app via a secure connection.</p>
            </div>
          )}
          <button 
            className="retry-button" 
            onClick={() => window.location.reload()}
          >
            Retry Camera Access
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-scanner-container">
      <div className="scanner-wrapper">
        <Scanner
          onScan={handleScan}
          onError={handleError}
          constraints={{
            video: {
              facingMode: 'environment', // Use back camera
              width: { ideal: 640 },
              height: { ideal: 480 }
            }
          }}
          formats={[
            'qr_code',
            'aztec',
            'code_128',
            'code_39',
            'ean_13',
            'ean_8',
            'data_matrix'
          ]}
          components={{
            audio: true, // Play sound on successful scan
            finder: false // Use custom overlay
          }}
          styles={{
            container: { 
              width: '100%',
              height: '100%',
              position: 'relative'
            },
            video: { 
              width: '100%', 
              height: '100%',
              objectFit: 'cover'
            }
          }}
        />
        
        {/* Custom scan overlay */}
        <div className="scan-overlay">
          <div className="scan-box">
            <div className="scan-corners">
              <div className="corner top-left"></div>
              <div className="corner top-right"></div>
              <div className="corner bottom-left"></div>
              <div className="corner bottom-right"></div>
            </div>
            {isScanning && (
              <div className="scan-success">
                <span>✓ Scanned!</span>
              </div>
            )}
          </div>
          
          <div className="scan-instructions">
            <p>Position QR code within the frame</p>
            <p className="tip">Hold steady for best results</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileQRScanner;